import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  Circle, 
  ArrowRight, 
  User, 
  MapPin, 
  Camera, 
  Briefcase, 
  Star, 
  Settings,
  Award,
  Clock,
  AlertCircle,
  Info,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { apiUrl } from '@/config/api';

interface ProfileCompletionStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  completed: boolean;
  required: boolean;
  weight: number; // How much this step contributes to overall completion
  visibility: 'external' | 'internal' | 'both'; // Whether this is visible externally, internally, or both
  action?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
}

interface ProfileCompletionData {
  overall_completion: number;
  completion_details: Record<string, boolean>;
  missing_steps: ProfileCompletionStep[];
  completed_steps: ProfileCompletionStep[];
  next_steps: ProfileCompletionStep[];
}

interface ProfileCompletionProps {
  userRole: 'tailor' | 'customer';
  onStepClick?: (stepId: string) => void;
  showDetailed?: boolean;
  compact?: boolean;
}

const ProfileCompletion: React.FC<ProfileCompletionProps> = ({
  userRole,
  onStepClick,
  showDetailed = true,
  compact = false
}) => {
  const { user, token } = useAuth();
  const [completionData, setCompletionData] = useState<ProfileCompletionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(!compact);

  useEffect(() => {
    fetchCompletionData();
  }, [user, token]);

  const fetchCompletionData = async () => {
    if (!user || !token) return;

    try {
      setIsLoading(true);
      const roleSegment =
        userRole === 'tailor'
          ? 'tailors'
          : userRole === 'customer'
            ? 'customers'
            : userRole;

      const response = await fetch(apiUrl(`/api/${roleSegment}/profile-completion`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompletionData(data);
      } else if (response.status === 404) {
        // Attempt legacy/universal endpoint for older deployments
        const universalResponse = await fetch(apiUrl(`/api/${roleSegment}/profile-completion/universal`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (universalResponse.ok) {
          const data = await universalResponse.json();
          setCompletionData(data);
        } else {
          setCompletionData(generateFallbackCompletionData());
        }
      } else {
        // Fallback to mock data if endpoint doesn't exist yet
        setCompletionData(generateFallbackCompletionData());
      }
    } catch (_error) {
      console.error('Error fetching completion data:', error);
      setCompletionData(generateFallbackCompletionData());
    } finally {
      setIsLoading(false);
    }
  };

  const generateFallbackCompletionData = (): ProfileCompletionData => {
    const steps = getStepsForRole(userRole);
    const completedSteps = steps.filter(step => Math.random() > 0.3); // Mock some completed steps
    const missingSteps = steps.filter(step => !completedSteps.includes(step));
    
    const overallCompletion = Math.round(
      (completedSteps.reduce((sum, step) => sum + step.weight, 0) / 
       steps.reduce((sum, step) => sum + step.weight, 0)) * 100
    );

    return {
      overall_completion: overallCompletion,
      completion_details: steps.reduce((acc, step) => {
        acc[step.id] = completedSteps.includes(step);
        return acc;
      }, {} as Record<string, boolean>),
      missing_steps: missingSteps,
      completed_steps: completedSteps,
      next_steps: missingSteps.slice(0, 3) // Show next 3 steps
    };
  };

  const getStepsForRole = (role: 'tailor' | 'customer'): ProfileCompletionStep[] => {
    if (role === 'tailor') {
      return [
        {
          id: 'basic_info',
          title: 'Basic Information',
          description: 'Complete your name, email, and phone number',
          icon: User,
          completed: false,
          required: true,
          weight: 20,
          visibility: 'both',
          action: {
            label: 'Complete',
            onClick: () => onStepClick?.('basic_info')
          }
        },
        {
          id: 'business_details',
          title: 'Business Details',
          description: 'Add your business name, tagline, and specialization',
          icon: Briefcase,
          completed: false,
          required: true,
          weight: 25,
          visibility: 'external',
          action: {
            label: 'Add Details',
            onClick: () => onStepClick?.('business_details')
          }
        },
        {
          id: 'profile_photo',
          title: 'Profile Photo',
          description: 'Upload a professional profile photo',
          icon: Camera,
          completed: false,
          required: false,
          weight: 15,
          visibility: 'external',
          action: {
            label: 'Upload Photo',
            onClick: () => onStepClick?.('profile_photo')
          }
        },
        {
          id: 'verification',
          title: 'Account Verification',
          description: 'Verify your identity and business credentials',
          icon: Award,
          completed: false,
          required: true,
          weight: 20,
          visibility: 'external',
          action: {
            label: 'Verify',
            onClick: () => onStepClick?.('verification')
          }
        },
        {
          id: 'portfolio',
          title: 'Portfolio',
          description: 'Add photos of your work and services',
          icon: Star,
          completed: false,
          required: false,
          weight: 10,
          visibility: 'external',
          action: {
            label: 'Add Portfolio',
            onClick: () => onStepClick?.('portfolio')
          }
        },
        {
          id: 'preferences',
          title: 'Preferences',
          description: 'Set your working hours, pickup radius, and preferences',
          icon: Settings,
          completed: false,
          required: false,
          weight: 10,
          visibility: 'internal',
          action: {
            label: 'Set Preferences',
            onClick: () => onStepClick?.('preferences')
          }
        }
      ];
    } else {
      return [
        {
          id: 'basic_info',
          title: 'Basic Information',
          description: 'Complete your name, email, and phone number',
          icon: User,
          completed: false,
          required: true,
          weight: 25,
          visibility: 'both',
          action: {
            label: 'Complete',
            onClick: () => onStepClick?.('basic_info')
          }
        },
        {
          id: 'address',
          title: 'Address',
          description: 'Add your delivery address',
          icon: MapPin,
          completed: false,
          required: true,
          weight: 20,
          visibility: 'internal',
          action: {
            label: 'Add Address',
            onClick: () => onStepClick?.('address')
          }
        },
        {
          id: 'profile_photo',
          title: 'Profile Photo',
          description: 'Upload a profile photo',
          icon: Camera,
          completed: false,
          required: false,
          weight: 15,
          visibility: 'external',
          action: {
            label: 'Upload Photo',
            onClick: () => onStepClick?.('profile_photo')
          }
        },
        {
          id: 'measurements',
          title: 'Body Measurements',
          description: 'Add your body measurements for better fitting',
          icon: User,
          completed: false,
          required: false,
          weight: 20,
          visibility: 'internal',
          action: {
            label: 'Add Measurements',
            onClick: () => onStepClick?.('measurements')
          }
        },
        {
          id: 'preferences',
          title: 'Preferences',
          description: 'Set your style preferences and notifications',
          icon: Settings,
          completed: false,
          required: false,
          weight: 10,
          visibility: 'internal',
          action: {
            label: 'Set Preferences',
            onClick: () => onStepClick?.('preferences')
          }
        },
        {
          id: 'verification',
          title: 'Account Verification',
          description: 'Verify your email and phone number',
          icon: Award,
          completed: false,
          required: false,
          weight: 10,
          visibility: 'external',
          action: {
            label: 'Verify',
            onClick: () => onStepClick?.('verification')
          }
        }
      ];
    }
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getCompletionMessage = (percentage: number) => {
    if (percentage >= 100) return "🎉 Profile Complete!";
    if (percentage >= 80) return "Almost there! Just a few more steps.";
    if (percentage >= 50) return "Good progress! Keep going.";
    return "Let's get started! Complete your profile to unlock all features.";
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (!completionData) {
    return null;
  }

  const { overall_completion, next_steps, completed_steps } = completionData;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${getCompletionColor(overall_completion)}`}>
              <User className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Profile Completion</h3>
              <p className="text-sm text-gray-600">{getCompletionMessage(overall_completion)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className={`text-2xl font-bold ${getCompletionColor(overall_completion).split(' ')[0]}`}>
                {overall_completion}%
              </div>
              <div className="text-xs text-gray-500">Complete</div>
            </div>
            {compact && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className={`h-2 rounded-full ${
                overall_completion >= 80 ? 'bg-green-500' :
                overall_completion >= 50 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              initial={{ width: 0 }}
              animate={{ width: `${overall_completion}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>

      {/* Detailed Steps */}
      <AnimatePresence>
        {isExpanded && showDetailed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-6">
              {/* External Profile Section */}
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <ExternalLink className="w-4 h-4 text-blue-600 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">External Profile</h4>
                  <span className="text-xs text-gray-500 ml-2">(Visible to customers)</span>
                </div>
                
                {/* External Completed Steps */}
                {completed_steps.filter(step => step.visibility === 'external' || step.visibility === 'both').length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
                      Completed ({completed_steps.filter(step => step.visibility === 'external' || step.visibility === 'both').length})
                    </h5>
                    <div className="space-y-2">
                      {completed_steps.filter(step => step.visibility === 'external' || step.visibility === 'both').map((step) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-400"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{step.title}</div>
                            <div className="text-xs text-gray-600">{step.description}</div>
                          </div>
                          <div className="text-xs text-green-600 font-medium">+{step.weight}%</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* External Next Steps */}
                {next_steps.filter(step => step.visibility === 'external' || step.visibility === 'both').length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <Clock className="w-3 h-3 text-blue-500 mr-1" />
                      Next Steps ({next_steps.filter(step => step.visibility === 'external' || step.visibility === 'both').length})
                    </h5>
                    <div className="space-y-2">
                      {next_steps.filter(step => step.visibility === 'external' || step.visibility === 'both').map((step, index) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400 hover:bg-blue-100 transition-colors"
                        >
                          <div className="flex-shrink-0">
                            {step.required ? (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900">{step.title}</div>
                              {step.required && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                  Required
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">{step.description}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="text-xs text-gray-500">+{step.weight}%</div>
                            {step.action && (
                              <button
                                onClick={step.action.onClick}
                                disabled={step.action.disabled}
                                className="text-xs bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {step.action.label}
                              </button>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Internal Profile Section */}
              <div>
                <div className="flex items-center mb-4">
                  <Settings className="w-4 h-4 text-purple-600 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">MasterJi Profile</h4>
                  <span className="text-xs text-gray-500 ml-2">(Internal settings)</span>
                </div>
                
                {/* Internal Completed Steps */}
                {completed_steps.filter(step => step.visibility === 'internal' || step.visibility === 'both').length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
                      Completed ({completed_steps.filter(step => step.visibility === 'internal' || step.visibility === 'both').length})
                    </h5>
                    <div className="space-y-2">
                      {completed_steps.filter(step => step.visibility === 'internal' || step.visibility === 'both').map((step) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-400"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{step.title}</div>
                            <div className="text-xs text-gray-600">{step.description}</div>
                          </div>
                          <div className="text-xs text-green-600 font-medium">+{step.weight}%</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Internal Next Steps */}
                {next_steps.filter(step => step.visibility === 'internal' || step.visibility === 'both').length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <Clock className="w-3 h-3 text-purple-500 mr-1" />
                      Next Steps ({next_steps.filter(step => step.visibility === 'internal' || step.visibility === 'both').length})
                    </h5>
                    <div className="space-y-2">
                      {next_steps.filter(step => step.visibility === 'internal' || step.visibility === 'both').map((step, index) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg border-l-4 border-purple-400 hover:bg-purple-100 transition-colors"
                        >
                          <div className="flex-shrink-0">
                            {step.required ? (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900">{step.title}</div>
                              {step.required && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                  Required
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">{step.description}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="text-xs text-gray-500">+{step.weight}%</div>
                            {step.action && (
                              <button
                                onClick={step.action.onClick}
                                disabled={step.action.disabled}
                                className="text-xs bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {step.action.label}
                              </button>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Completion Tips */}
              {overall_completion < 100 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Profile Completion Tips</h5>
                      <div className="text-xs text-blue-700 mt-1">
                        {userRole === 'tailor' ? (
                          <>
                            • Complete verification to build customer trust<br/>
                            • Add portfolio photos to showcase your work<br/>
                            • Set preferences to match with right customers
                          </>
                        ) : (
                          <>
                            • Add measurements for better garment fitting<br/>
                            • Verify your account for secure transactions<br/>
                            • Set preferences for personalized recommendations
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileCompletion;
